import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

interface Country {
  code: string;
  name: string;
  priceModifier: string;
  hidden?: boolean;
  requiresSixBottleMultiples?: boolean;
}

export default function CountryPriceManager() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>('');
  const [editingCountry, setEditingCountry] = useState<string | null>(null);
  const [priceModifier, setPriceModifier] = useState('');
  const [deletingCountry, setDeletingCountry] = useState<string | null>(null);
  const [pendingVisibilityChanges, setPendingVisibilityChanges] = useState<Record<string, boolean>>({});
  const [localHiddenCountries, setLocalHiddenCountries] = useState<Record<string, boolean>>({});
  const [pendingBottleMultiplesChanges, setPendingBottleMultiplesChanges] = useState<Record<string, boolean>>({});
  const [localBottleMultiplesCountries, setLocalBottleMultiplesCountries] = useState<Record<string, boolean>>({});

  const { data: countries, isLoading: isLoadingCountries, refetch: refetchCountries, isFetching } = useQuery<Country[]>({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await fetch('/api/countries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) throw new Error('Error carregant països');
      return response.json();
    }
  });

  useEffect(() => {
    if (!countries) return;
    const hiddenCountriesMap: Record<string, boolean> = {};
    const bottleMultiplesCountriesMap: Record<string, boolean> = {};
    countries.forEach((country: Country) => {
      if (country.hidden) hiddenCountriesMap[country.code] = true;
      if (country.requiresSixBottleMultiples) bottleMultiplesCountriesMap[country.code] = true;
    });
    setLocalHiddenCountries(hiddenCountriesMap);
    setLocalBottleMultiplesCountries(bottleMultiplesCountriesMap);
  }, [countries]);

  const updatePricing = useMutation({
    mutationFn: async ({ countryCode, modifier }: { countryCode: string; modifier: string }) => {
      const response = await fetch(`/api/countries/${countryCode}/pricing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ priceModifier: modifier })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error actualitzant el preu');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      setEditingCountry(null);
      setPriceModifier('');
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const updateVisibility = useMutation({
    mutationFn: async ({ countryCode, hidden }: { countryCode: string; hidden: boolean }) => {
      setPendingVisibilityChanges(prev => ({
        ...prev,
        [countryCode]: hidden
      }));
      
      setLocalHiddenCountries(prev => ({
        ...prev,
        [countryCode]: hidden
      }));
      
      const response = await fetch(`/api/countries/${countryCode}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ hidden })
      });
      
      if (!response.ok) {
        setPendingVisibilityChanges(prev => {
          const newState = { ...prev };
          delete newState[countryCode];
          return newState;
        });
        setLocalHiddenCountries(prev => {
          const newState = { ...prev };
          if (!hidden) {
            newState[countryCode] = true;
          } else {
            delete newState[countryCode];
          }
          return newState;
        });
        
        const error = await response.json();
        throw new Error(error.error || 'Error actualitzant la visibilitat');
      }
      
      return { countryCode, hidden };
    },
    onSuccess: (data) => {
      setPendingVisibilityChanges(prev => {
        const newState = { ...prev };
        delete newState[data.countryCode];
        return newState;
      });
      
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const updateSixBottleMultiples = useMutation({
    mutationFn: async ({ countryCode, required }: { countryCode: string; required: boolean }) => {
      setPendingBottleMultiplesChanges(prev => ({
        ...prev,
        [countryCode]: required
      }));
      
      setLocalBottleMultiplesCountries(prev => ({
        ...prev,
        [countryCode]: required
      }));
      
      const response = await fetch(`/api/countries/${countryCode}/six-bottle-multiples`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ required })
      });
      
      if (!response.ok) {
        setPendingBottleMultiplesChanges(prev => {
          const newState = { ...prev };
          delete newState[countryCode];
          return newState;
        });
        setLocalBottleMultiplesCountries(prev => {
          const newState = { ...prev };
          if (!required) {
            newState[countryCode] = true;
          } else {
            delete newState[countryCode];
          }
          return newState;
        });
        
        const error = await response.json();
        throw new Error(error.error || 'Error actualitzant la configuració de múltiples de 6');
      }
      
      return { countryCode, required };
    },
    onSuccess: (data) => {
      setPendingBottleMultiplesChanges(prev => {
        const newState = { ...prev };
        delete newState[data.countryCode];
        return newState;
      });
      
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const deletePricing = useMutation({
    mutationFn: async (countryCode: string) => {
      const response = await fetch(`/api/countries/${countryCode}/pricing`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Error eliminant el modificador de preu');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      setDeletingCountry(null);
      setError('');
    },
    onError: (error: Error) => {
      setError(error.message);
    }
  });

  const handleEdit = (country: Country) => {
    setEditingCountry(country.code);
    setPriceModifier(country.priceModifier || '');
    setError('');
  };

  const handleSave = (countryCode: string) => {
    updatePricing.mutate({ countryCode, modifier: priceModifier });
  };

  const handleDelete = (countryCode: string) => {
    setDeletingCountry(countryCode);
  };

  const confirmDelete = (countryCode: string) => {
    deletePricing.mutate(countryCode);
  };

  const cancelDelete = () => {
    setDeletingCountry(null);
  };

  const toggleVisibility = (country: Country) => {
    const isCurrentlyHidden = 
      pendingVisibilityChanges.hasOwnProperty(country.code) ? 
      pendingVisibilityChanges[country.code] : 
      localHiddenCountries[country.code] || country.hidden || false;
    
    updateVisibility.mutate({ 
      countryCode: country.code, 
      hidden: !isCurrentlyHidden 
    });
  };

  const toggleSixBottleMultiples = (country: Country) => {
    const isCurrentlyRequired = 
      pendingBottleMultiplesChanges.hasOwnProperty(country.code) ? 
      pendingBottleMultiplesChanges[country.code] : 
      localBottleMultiplesCountries[country.code] || 
      country.requiresSixBottleMultiples || 
      false;
    
    updateSixBottleMultiples.mutate({ 
      countryCode: country.code, 
      required: !isCurrentlyRequired 
    });
  };

  if (isLoadingCountries) {
    return <div className="text-center py-8">Carregant...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Configuració de preus per país</h2>
        <button
          onClick={() => refetchCountries()}
          disabled={isFetching}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
          title="Actualitzar llista de països"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {isFetching ? 'Actualitzant...' : 'Actualitzar'}
        </button>
      </div>
      <p className="mb-4 text-sm text-gray-600">
        Configureu el modificador de preu per cada país. Utilitzeu expressions matemàtiques com "*1.1" per augmentar un 10% o "*0.9" per reduir un 10%.
      </p>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">País</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modificador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibilitat</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Múltiples de 6</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Accions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {countries?.map((country) => {
                const isHidden = 
                  pendingVisibilityChanges.hasOwnProperty(country.code) ? 
                  pendingVisibilityChanges[country.code] : 
                  localHiddenCountries[country.code] || 
                  country.hidden || 
                  false;
                
                const requiresMultiples = 
                  pendingBottleMultiplesChanges.hasOwnProperty(country.code) ? 
                  pendingBottleMultiplesChanges[country.code] : 
                  localBottleMultiplesCountries[country.code] || 
                  country.requiresSixBottleMultiples || 
                  false;
                  
                return (
                  <tr key={country.code} className={isHidden ? "bg-gray-50" : ""}>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 break-words max-w-xs">{decodeHtmlEntities(country.name)}</div>
                      <div className="text-sm text-gray-500">{country.code}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCountry === country.code ? (
                        <input
                          type="text"
                          value={priceModifier}
                          onChange={(e) => setPriceModifier(e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Ex: *1.1"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">{country.priceModifier}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleVisibility(country)}
                        className="flex items-center"
                        title={isHidden ? "Mostrar país" : "Ocultar país"}
                        disabled={updateVisibility.isPending && pendingVisibilityChanges.hasOwnProperty(country.code)}
                      >
                        <div className={`relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in ${updateVisibility.isPending && pendingVisibilityChanges.hasOwnProperty(country.code) ? "opacity-50" : ""}`}>
                          <input
                            type="checkbox"
                            name={`toggle-${country.code}`}
                            id={`toggle-${country.code}`}
                            checked={!isHidden}
                            onChange={() => {}}
                            className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                          />
                          <label
                            htmlFor={`toggle-${country.code}`}
                            className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${!isHidden ? "bg-green-400" : "bg-gray-300"}`}
                          ></label>
                        </div>
                        <span className="text-sm text-gray-700">
                          {isHidden ? "Ocult" : "Visible"}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleSixBottleMultiples(country)}
                        className="flex items-center"
                        title={requiresMultiples ? "Desactivar múltiples de 6" : "Activar múltiples de 6"}
                        disabled={updateSixBottleMultiples.isPending && pendingBottleMultiplesChanges.hasOwnProperty(country.code)}
                      >
                        <div className={`relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in ${updateSixBottleMultiples.isPending && pendingBottleMultiplesChanges.hasOwnProperty(country.code) ? "opacity-50" : ""}`}>
                          <input
                            type="checkbox"
                            name={`bottle-toggle-${country.code}`}
                            id={`bottle-toggle-${country.code}`}
                            checked={requiresMultiples}
                            onChange={() => {}}
                            className="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer"
                          />
                          <label
                            htmlFor={`bottle-toggle-${country.code}`}
                            className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer ${requiresMultiples ? "bg-green-400" : "bg-gray-300"}`}
                          ></label>
                        </div>
                        <span className="text-sm text-gray-700">
                          {requiresMultiples ? "Actiu" : "Inactiu"}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingCountry === country.code ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleSave(country.code)}
                            className="text-green-600 hover:text-green-900"
                            disabled={updatePricing.isPending}
                          >
                            Desar
                          </button>
                          <button
                            onClick={() => {
                              setEditingCountry(null);
                              setPriceModifier('');
                              setError('');
                            }}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel·lar
                          </button>
                        </div>
                      ) : deletingCountry === country.code ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => confirmDelete(country.code)}
                            className="text-red-600 hover:text-red-900"
                            disabled={deletePricing.isPending}
                          >
                            Confirmar
                          </button>
                          <button
                            onClick={cancelDelete}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Cancel·lar
                          </button>
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(country)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Editar"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          {country.priceModifier && (
                            <button
                              onClick={() => handleDelete(country.code)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}